/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import $ from './Util';

/**
 * Represents a Navial date handler class.
 */

export default class Datetime {

    /** Month names. */
    static get MONTH() { return {
        hu: ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december']
    };}

    /** Day names. */
    static get DAY() { return {
        hu: ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat']
    };}

    /** Short day names. */
    static get SHORTDAY() { return {
        hu: ['v', 'h', 'k', 'sz', 'cs', 'p', 'szo']
    };}
    
    /**
     * Creates a new Datetime object and sets its attributes.
     * 
     * @param   {Date}      date        native date object
     */

    constructor(date) {

        // Validating the date

        if ($.isEmpty(date) || !$.isDate(date)) {
            throw new Error('date can not be empty and must be a Date object');
        }

        // Storing the date attributes

        this._date = date;
    }

    /**
     * Parses the specified date by using the pattern. If any of the parameters
     * are not defined or not a string, it returns null. The pattern can be
     * contains yyyy, mm or dd texts. Any other texts will be ommited.
     *
     * @param   {String}  date      date that has to parse
     * @param   {String}  pattern   pattern format using during the parsing
     * @return  {Date}    result    parsed date
     */

    static parseDate(date, pattern) {

        // Checking the parameters

        if ($.isEmpty(date) || $.isEmpty(pattern) ||
        !$.isString(date) || !$.isString(pattern) || date === '') {
            return null;
        }

        // Preparing

        let year;
        let month;
        let day;

        // Processing the pattern

        for (let i = 0; i < pattern.length; i++) {
            if (pattern[i] === 'y') {
                year = parseInt(date.substring(i, i + 4), 10);
                i+=3;
            } else if (pattern[i] === 'm') {
                month = parseInt(date.substring(i, i + 2), 10) - 1;
                i++;
            } else if (pattern[i] === 'd') {
                day = parseInt(date.substring(i, i + 2), 10);
                i++;
            }
        }

        // Creating the date

        let parsedDate = new Date(year, month, day);
        
        // Checking the date

        if (isNaN(parsedDate.getTime()) || $.isEmpty(parsedDate)) {
            return null;
        }

        let nDate = new Datetime(parsedDate);

        if (nDate.format(pattern) === date) {

            // Pattern and the date were valid. Returns the parsed date.

            return nDate.toDate();
        } else {

            // Pattern or date was i$lid. Returns null.
            
            return null;
        }
    }

    /**
     * Does the specified date string matches the specified pattern and is this
     * date valid? If any of the parameters are not defined or not a string, it
     * returns null. The pattern can be contains yyyy, mm or dd texts.
     *
     * @param   {String}  date      date that has to check
     * @param   {String}  pattern   pattern format using during the check
     * @return  {Boolean} result    date is valid or not
     */

    static isValidDate(date, pattern) {

        // Checking the parameters

        if ($.isEmpty(date) || $.isEmpty(pattern) ||
        !$.isString(date) || !$.isString(pattern)) {
            return false;
        }

        // Parsing the date

        var parsedDate = Datetime.parseDate(date, pattern);

        // Returns that the date is valid or not

        return parsedDate !== null;
    }

    /**
     * Converts this Datetime object to date.
     * 
     * @return  {Date}  date    date representation of the datetime
     */

    toDate() {
        return this._date;
    }

    /**
     * Formats this date by using the specified pattern. The pattern can be
     * contains yyyy, mm or dd texts. Any other texts will copied to the
     * result string. If the parameter is not defined or not a date, it
     * returns null.
     *
     * @param   {String}  pattern   format pattern
     * @return  {String}  result    formatted date
     */

    format(pattern) {

        // Checking the parameter

        if ($.isEmpty(pattern) || !$.isString(pattern)) {
            return null;
        }
        
        // Preparing

        let text = '';

        // Processing the pattern

        for (let i = 0; i < pattern.length; i++) {

            // Year

            if (pattern[i] === 'y') {
                text += this._date.getFullYear();
                i+=3;
            
            // Month

            } else if (pattern[i] === 'm') {
                text += ('' + (this._date.getMonth() + 1)).padStart(2, '0');
                i++;
            
            // Day

            } else if (pattern[i] === 'd') {
                text += ('' + this._date.getDate()).padStart(2, '0');
                i++;

            // Hour

            } else if (pattern[i] === 'h') {
                text += ('' + this._date.getHours()).padStart(2, '0');
                i++;

            // Minute

            } else if (pattern[i] === 'M') {
                text += ('' + this._date.getMinutes()).padStart(2, '0');
                i++;

            // Second

            } else if (pattern[i] === 's') {
                text += ('' + this._date.getSeconds()).padStart(2, '0');
                i++;

            } else {
                text += pattern[i];
            }
        }

        // Returns the formatted date

        return text === '' ? null : text;
    }

    /**
     * Adds the specified number of days to the date and returns the result.
     * The original date will be untouched. If the parameter is not defined or not
     * a number, it returns a null value.
     *
     * @param   {Number}  day       number of days that has to add
     * @return  {Date}    result    new date after the incrementing or decrementing
     */

    addDay(day) {

        // Checking the parameter

        if ($.isEmpty(day) || !$.isNumber(day)) {
            return null;
        }

        // Cloning the current date

        let newDate = new Date(this.toDate().getTime());
        
        // Adding the number of days

        newDate.setDate(newDate.getDate() + day);

        // Returns the new date

        return newDate;
    }

    /**
     * Adds the specified number of months to the date and returns the result.
     * The original date will be untouched. If the parameter is not defined or not
     * a number, it returns a null value.
     *
     * @param   {Number}  month     number of months that has to add
     * @return  {Date}    result    new date after the incrementing or decrementing
     */

    addMonth(month) {

        // Checking the parameter

        if ($.isEmpty(month) || !$.isNumber(month)) {
            return null;
        }

        // Cloning the current date

        let newDate = new Date(this.toDate().getTime());

        // Adding the number of months

        newDate.setMonth(newDate.getMonth() + month);

        // Returns the new date

        return newDate;
    }

    /**
     * Adds the specified number of years to the date and returns the result.
     * The original date will be untouched. If the parameter is not defined or not
     * a number, it returns a null value.
     *
     * @param   {Number}  year      number of years that has to add
     * @return  {Date}    result    new date after the incrementing or decrementing
     */

    addYear(year) {

        // Checking the parameter

        if ($.isEmpty(year) || !$.isNumber(year)) {
            return null;
        }
        
        // Cloning the current date

        let newDate = new Date(this.toDate().getTime());

        // Adding the number of years

        newDate.setYear(1900 + newDate.getYear() + year);

        // Returns the new date

        return newDate;
    }

    /**
     * Gets the difference in days between this and the specified date. If the
     * parameter is not defined or not a number, it returns a 0 value.
     * The time part of the dates will be ommitted.
     *
     * @param   {Number}  year      number of years that has to add
     * @return  {Number}  days      number of days between the dates
     */

    diff(date) {

        // Checking the parameter

        if ($.isEmpty(date) || !$.isDate(date)) {
            return 0;
        }
        
        // Omitting the time part of the dates

        let nDate = this.toDate();
        let date1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        let date2 = new Date(nDate.getFullYear(), nDate.getMonth(), nDate.getDate());

        // Returns the difference between the two dates

        return (date1 - date2) / 1000 / 60 / 60 / 24;
    }

    /**
     * Compares this and the specified date and returns true if the year, month and
     * day parts of the dates are equal. If the parameter is not defined or not a
     * date, it returns false.
     *
     * @param   {Date}    date      date that has to compare
     * @return  {Boolean} result    comparison result of the two dates
     */

    isEqual(date) {

        // Checking the parameter

        if ($.isEmpty(date) || !$.isDate(date)) {
            return false;
        }

        // Returns the comparison of the two dates

        let nDate = this.toDate();

        return  date.getFullYear() === nDate.getFullYear() && 
                date.getMonth() === nDate.getMonth() && 
                date.getDate() === nDate.getDate();     
    }

    /**
     * Checks the specified date that is it earlier date than the current one or
     * not. If the parameter is not defined or not a date, it returns false.
     *
     * @param   {Date}    date      date that has to check
     * @return  {Boolean} result    comparison result of the two dates
     */

    isBefore(date) {

        // Checking the parameter

        if ($.isEmpty(date) || !$.isDate(date)) {
            return false;
        }

        // Omitting the time part of the dates

        let nDate = this.toDate();
        let date1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        let date2 = new Date(nDate.getFullYear(), nDate.getMonth(), nDate.getDate());

        // Returns the comparision result of the two dates

        return (date1.getTime() - date2.getTime()) < 0;
    }
    
    /**
     * Checks the specified date that is it after than the current one or
     * not. If the parameter is not defined or not a date, it returns false.
     *
     * @param   {Date}    date      date that has to check
     * @return  {Boolean} result    comparison result of the two dates
     */

    isAfter(date) {

        // Checking the parameter

        if ($.isEmpty(date) || !$.isDate(date)) {
            return false;
        }

        // Omitting the time part of the dates

        let nDate = this.toDate();
        let date1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        let date2 = new Date(nDate.getFullYear(), nDate.getMonth(), nDate.getDate());

        // Returns the comparision result of the two dates

        return (date1.getTime() - date2.getTime()) > 0;
    }
};


// WEBPACK FOOTER //
// ./src/components/Util/Datetime.js